import { useEffect, useState } from "react";
import { get } from "lodash";
import { isHaveActionPermission } from "utills/helpers";
import { DownloadIcon } from "@heroicons/react/outline";
import { customerAccountApi } from "routes/customerAccount/api/service.api";
import useQueryParams from "routes/customerAccount/hooks/useQueryParams";
import { useGetList } from "routes/customerAccount/hooks/useGetList";
import { useExportExcel } from "routes/customerAccount/hooks/useExportExcel";
import { Breadcrumbs } from "components/breadcrumbs";
import Button from "components/button";
import { FullScreenLoading } from "components/fullScreenLoading";
import FilterBar, {
  FilterType,
} from "routes/customerAccount/components/FilterBar";
import DataTable from "routes/customerAccount/components/DataTable";

const CustomerAccountListPage = () => {
  const PAGE_SIZE = 10;

  const { searchParams, paramsObject, setQueryParams } = useQueryParams();

  const page = searchParams.get("page") ? Number(searchParams.get("page")) : 1;
  const searchQuery = searchParams.get("q") ?? "";
  const createdBtQuery = searchParams.get("created_by") ?? "";
  const fromDateQuery = searchParams.get("from_date") ?? "";
  const toDateQuery = searchParams.get("to_date") ?? "";

  const { data, loading, paging, params, setParams, fetchData } = useGetList({
    page: page,
    q: searchQuery,
    created_by: createdBtQuery,
    from_date: fromDateQuery,
    to_date: toDateQuery,
    limit: PAGE_SIZE,
  });

  const { loading: exportLoading, fetchData: exportFetchData } =
    useExportExcel();

  const [filter, setFilter] = useState<FilterType>({
    page: page,
    q: searchQuery,
    created_by: createdBtQuery,
    from_date: fromDateQuery,
    to_date: toDateQuery,
  });

  const [exportId, setExportId] = useState<string | null>();

  const handleChangeFilter = (key: string, value: string, value2?: string) => {
    // If key is "created_at" ===> value: from_date, value2: to_date
    if (key === "created_at") {
      setFilter({
        ...filter,
        from_date: value,
        to_date: value2 ?? "",
      });
    } else {
      setFilter({
        ...filter,
        [key]: value,
      });
    }
  };

  const handleChangePage = (page: number) => {
    setFilter({ ...filter, page: page });
    // Update URL search params
    setQueryParams({
      page: page.toString(),
    });
    setParams({ ...params, page: page }); // Update API params
  };

  const handleSearch = async (data: FilterType) => {
    console.log("Search:", data);

    // Update URL params
    setQueryParams({
      page: "1",
      q: filter.q,
      created_by: filter.created_by,
      from_date: filter.from_date,
      to_date: filter.to_date,
    });

    // Update API params
    setParams({
      page: 1,
      q: filter.q,
      created_by: filter.created_by,
      from_date: filter.from_date,
      to_date: filter.to_date,
      limit: PAGE_SIZE,
    });
  };

  const handleExportExcel = async () => {
    const res = await exportFetchData({
      q: filter.q,
      created_by: filter.created_by,
      from_date: filter.from_date,
      to_date: filter.to_date,
    });

    if (res.is_success) setExportId(res.export_id);
  };

  // Handle Export Excel
  useEffect(() => {
    let intervalId: any;

    if (exportId) {
      intervalId = setInterval(() => {
        customerAccountApi
          .getExportResult(exportId)
          .then((data) => {
            const isSuccess = get(data, "data.data.is_success", false);
            if (isSuccess) {
              setExportId(null);
              window.location.href = get(data, "data.data.file_link", "#");
            }
          })
          .catch((error) => {
            setExportId(null);
          });
      }, 2000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [exportId, customerAccountApi.getExportResult]);

  return (
    <section className="wrapper">
      {loading && <FullScreenLoading />}

      <Breadcrumbs
        customPageLabel="Customer Accounts Management"
        additionalLabel={
          <div className="flex gap-x-3">
            {isHaveActionPermission("", "customer_accounts", "export") && (
              <Button
                className="w-11 h-11 sm:w-auto"
                onClick={() => {
                  handleExportExcel();
                }}
                loading={exportLoading || (exportId ? true : false)}
              >
                <DownloadIcon className="w-4 h-4 text-white" />{" "}
                <span className="hidden sm:block sm:ml-2">Export Excel</span>
              </Button>
            )}
          </div>
        }
      />

      <FilterBar
        filter={filter}
        handleChangeFilter={handleChangeFilter}
        onSubmit={handleSearch}
      />

      <DataTable
        data={data}
        paging={paging}
        handleChangePage={handleChangePage}
      />
    </section>
  );
};

export default CustomerAccountListPage;
